/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useMemo } from 'react';

import { jsx, cssMap } from '@compiled/react';
import { useIntl } from 'react-intl';

import { aiSuggestionsMessages } from '@atlaskit/editor-common/ai-messages';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { token } from '@atlaskit/tokens';
import { useUIStore } from '@atlassian/conversation-assistant-store/controllers/ui-store';
import { RovoThinkingBarWrapper } from '@atlassian/rovo-platform-ui-components/loading-state/RovoThinkingBarWrapper';
import { RovoThinkingReadyTransition } from '@atlassian/rovo-platform-ui-components/loading-state/RovoThinkingReadyTransition';

import type { AiSuggestionsPlugin } from '../../../aiSuggestionsPluginType';
import { useSkill } from '../../hooks/useSkill';
import { useSyncedThinkingStatus } from '../../hooks/useSyncedThinkingStatus';

import { getThinkingRotatingMessages } from './thinkingMessages';

type ThinkingContentComponentProps = {
	api: ExtractInjectionAPI<AiSuggestionsPlugin> | undefined;
};

const AUTO_CLOSE_DELAY_MS = 3000;

const styles = cssMap({
	container: {
		position: 'fixed',
		bottom: token('space.300'),
		minWidth: 400,
		zIndex: 510, // same as akEditorFloatingDialogZIndex
		left: '50%',
		transform: 'translateX(-50%)',
	},
	hidden: {
		display: 'none',
	},
});

export function ThinkingContentComponent({
	api,
}: ThinkingContentComponentProps): JSX.Element | null {
	const { formatMessage } = useIntl();
	const thinkingStatus = useSyncedThinkingStatus(api);
	/**
	 * We use this to detect when to hide / show the thinking component
	 */
	const { mounted: isRovoMounted } = useUIStore();

	const { firstSuggestionId } = useSharedPluginStateWithSelector(
		api,
		['aiSuggestions'],
		(state) => ({
			firstSuggestionId: state?.aiSuggestionsState?.orderedSuggestionIds?.[0],
		}),
	);

	const { abortActiveConversation } = useSkill({
		api,
		thinkingStatus,
		isRovoMounted,
	});

	const handleStop = useCallback(() => {
		api?.aiSuggestions?.actions.stopThinking();
		abortActiveConversation();
	}, [api, abortActiveConversation]);

	const onViewSuggestions = useCallback(() => {
		api?.aiSuggestions?.actions.stopThinking();
		api?.aiSuggestions?.actions.selectSuggestion(firstSuggestionId ?? null);
	}, [api, firstSuggestionId]);

	const customRotatingMessages = useMemo(
		() => getThinkingRotatingMessages(formatMessage),
		[formatMessage],
	);

	// When the auto-close timer fires, only dismiss (return to idle) if there
	// are no suggestions — matching the legacy ThinkingTransitionWrapper
	// behaviour where the complete bar stayed visible until the user clicked
	// "View Suggestions" or "Close".
	const handleClose = useCallback(() => {
		api?.aiSuggestions?.actions.stopThinking();
	}, [api]);

	const isReady = thinkingStatus === 'complete';
	const hasSuggestions = !!firstSuggestionId;

	const readyText = hasSuggestions
		? formatMessage(aiSuggestionsMessages.thinkingCompleteReviewComplete)
		: formatMessage(aiSuggestionsMessages.thinkingCompleteNoSuggestions);

	const thinkingBarProps = useMemo(
		() => ({
			onStop: handleStop,
			showProgressBar: true,
			useRotatingTitle: true,
			customRotatingMessages,
		}),
		[handleStop, customRotatingMessages],
	);

	const thinkingReadyProps = useMemo(
		() => ({
			onClose: handleClose,
			onView: hasSuggestions ? onViewSuggestions : undefined,
			readyText,
		}),
		[handleClose, hasSuggestions, onViewSuggestions, readyText],
	);

	if (thinkingStatus === 'idle') {
		return null;
	}

	return (
		<div css={[styles.container, isRovoMounted && styles.hidden]}>
			<RovoThinkingBarWrapper isThinking={thinkingStatus === 'thinking'}>
				<RovoThinkingReadyTransition
					isReady={isReady}
					autoClose={!hasSuggestions}
					autoCloseDelay={AUTO_CLOSE_DELAY_MS}
					thinkingBarProps={thinkingBarProps}
					thinkingReadyProps={thinkingReadyProps}
				/>
			</RovoThinkingBarWrapper>
		</div>
	);
}
